import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Mail, Save, Send, ShieldAlert, Loader2, AtSign, KeyRound,
} from "lucide-react";
import RichText from "./RichText";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/admin`;
const ax = axios.create({ baseURL: API, withCredentials: true });
const MASK = "••••••••";

const inputCls =
  "w-full rounded-xl border border-navy-100 bg-white px-4 py-3 text-sm text-navy-800 placeholder:text-navy-400 focus:outline-none focus:border-teal focus:ring-2 focus:ring-teal/20";

export default function EmailSettings() {
  const [doc, setDoc] = useState(null);
  const [busy, setBusy] = useState(false);
  const [apiKey, setApiKey] = useState("");
  const [testTo, setTestTo] = useState("");

  useEffect(() => {
    ax.get("/email-settings").then(({ data }) => setDoc(data)).catch(() => toast.error("Failed to load settings"));
  }, []);

  if (!doc) {
    return <div className="py-16 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-teal" /></div>;
  }

  const update = (patch) => setDoc((d) => ({ ...d, ...patch }));
  const updateTemplate = (key, patch) =>
    setDoc((d) => ({ ...d, templates: { ...(d.templates || {}), [key]: { ...(d.templates?.[key] || {}), ...patch } } }));

  const save = async () => {
    setBusy(true);
    try {
      const payload = { ...doc };
      delete payload._id;
      delete payload.api_key_in_env;
      // Only send a new api_key if the user typed something other than the mask.
      payload.api_key = apiKey && apiKey !== MASK ? apiKey : "";
      const { data } = await ax.put("/email-settings", payload);
      setDoc(data);
      setApiKey("");
      toast.success("Email settings saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const sendTest = async () => {
    if (!testTo) return toast.error("Enter a recipient email first");
    setBusy(true);
    try {
      const { data } = await ax.post("/email-settings/test", { to: testTo });
      if (data.sent) toast.success(`Test email sent to ${testTo}`);
      else toast.error(`Could not send: ${data.reason || "unknown"}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Test send failed");
    } finally {
      setBusy(false);
    }
  };

  const hasKey = !!doc.api_key || apiKey || doc.api_key_in_env;

  return (
    <div className="max-w-4xl">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="font-display font-bold text-3xl text-navy-800 inline-flex items-center gap-2">
            <Mail className="h-7 w-7 text-teal" /> Email Settings
          </h1>
          <p className="mt-2 text-navy-700/70">Auto-acknowledgement emails for contact and quote submissions.</p>
        </div>
        <div className="flex items-center gap-2">
          <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-semibold uppercase tracking-wider ${
            doc.enabled ? "bg-teal/10 text-teal" : "bg-navy/10 text-navy-700"
          }`} data-testid="email-status-pill">
            <span className={`h-1.5 w-1.5 rounded-full ${doc.enabled ? "bg-teal" : "bg-navy-300"}`} />
            {doc.enabled ? "Live" : "Disabled"}
          </span>
        </div>
      </header>

      {/* Provider + key */}
      <section className="mt-8 p-6 rounded-3xl bg-white border border-navy-50 shadow-soft">
        <h2 className="font-display font-semibold text-lg text-navy-800">Provider</h2>
        <div className="mt-5 grid gap-4 md:grid-cols-2">
          <label className="block">
            <span className="block text-[11px] uppercase tracking-[0.18em] font-semibold text-navy-500 mb-2">Provider</span>
            <select
              value={doc.provider || "resend"}
              onChange={(e) => update({ provider: e.target.value })}
              className={inputCls}
              data-testid="email-provider"
            >
              <option value="resend">Resend</option>
            </select>
          </label>
          <label className="block">
            <span className="block text-[11px] uppercase tracking-[0.18em] font-semibold text-navy-500 mb-2 inline-flex items-center gap-1.5">
              <KeyRound className="h-3.5 w-3.5" /> API key
            </span>
            <input
              type="password"
              value={apiKey || (doc.api_key ? MASK : "")}
              onChange={(e) => setApiKey(e.target.value)}
              placeholder={doc.api_key_in_env ? "Using RESEND_API_KEY from env" : "re_xxxxxxxx"}
              className={inputCls}
              data-testid="email-api-key"
              autoComplete="new-password"
            />
            <span className="mt-2 block text-[11px] text-navy-500">
              Stored encrypted at rest; never returned in plaintext. Leave as •••••••• to keep the existing key.
            </span>
          </label>
        </div>

        {!hasKey && (
          <div className="mt-5 flex items-start gap-2 p-3 rounded-xl bg-amber-50 border border-amber-200 text-[12px] text-amber-900" data-testid="email-no-key-banner">
            <ShieldAlert className="h-4 w-4 mt-0.5 shrink-0" />
            <p>No API key configured yet. Add one above or set <code className="font-mono">RESEND_API_KEY</code> in the backend env. Until then, acknowledgement emails will silently no-op.</p>
          </div>
        )}
      </section>

      {/* Sender identity + toggle */}
      <section className="mt-6 p-6 rounded-3xl bg-white border border-navy-50 shadow-soft">
        <h2 className="font-display font-semibold text-lg text-navy-800">Sender identity</h2>
        <div className="mt-5 grid gap-4 md:grid-cols-2">
          <label className="block">
            <span className="block text-[11px] uppercase tracking-[0.18em] font-semibold text-navy-500 mb-2">From name</span>
            <input value={doc.from_name || ""} onChange={(e) => update({ from_name: e.target.value })} className={inputCls} data-testid="email-from-name" />
          </label>
          <label className="block">
            <span className="block text-[11px] uppercase tracking-[0.18em] font-semibold text-navy-500 mb-2 inline-flex items-center gap-1.5">
              <AtSign className="h-3.5 w-3.5" /> From email
            </span>
            <input value={doc.from_email || ""} onChange={(e) => update({ from_email: e.target.value })} className={inputCls} data-testid="email-from-email" />
          </label>
          <label className="block">
            <span className="block text-[11px] uppercase tracking-[0.18em] font-semibold text-navy-500 mb-2">Response ETA</span>
            <input value={doc.response_eta || ""} onChange={(e) => update({ response_eta: e.target.value })} placeholder="1 business day" className={inputCls} data-testid="email-response-eta" />
          </label>
          <label className="flex items-center gap-3 pt-7">
            <input
              type="checkbox"
              checked={!!doc.enabled}
              onChange={(e) => update({ enabled: e.target.checked })}
              className="h-5 w-5 accent-teal"
              data-testid="email-enabled-toggle"
            />
            <span className="text-sm text-navy-800 font-semibold">Send acknowledgement emails</span>
          </label>
        </div>
        <p className="mt-4 text-[12px] text-navy-500" data-testid="email-sandbox-note">
          Sending from {" "}
          <code className="font-mono">info@infinitymg.so</code> requires verifying the {" "}
          <code className="font-mono">infinitymg.so</code> domain in your Resend dashboard.
          Until verified, Resend will reject sends — keep <strong>Send acknowledgement emails</strong>
          turned off (ack emails silently no-op) or temporarily switch the from-email
          to <code className="font-mono">onboarding@resend.dev</code> for testing.
        </p>
      </section>

      {/* Templates */}
      <section className="mt-6 p-6 rounded-3xl bg-white border border-navy-50 shadow-soft">
        <h2 className="font-display font-semibold text-lg text-navy-800">Templates</h2>
        <p className="mt-2 text-sm text-navy-700/70">
          Available placeholders: <code>{"{{name}}"}</code>, <code>{"{{subject}}"}</code>, <code>{"{{service_interest}}"}</code>, <code>{"{{response_eta}}"}</code>.
        </p>

        {["contact_ack", "quote_ack"].map((key) => {
          const t = doc.templates?.[key] || {};
          const label = key === "contact_ack" ? "Contact form acknowledgement" : "Quote request acknowledgement";
          return (
            <div key={key} className="mt-6 rounded-2xl border border-navy-100 bg-soft-gray/40 p-5" data-testid={`email-template-${key}`}>
              <h3 className="font-display font-semibold text-navy-800">{label}</h3>
              <label className="mt-4 block">
                <span className="block text-[11px] uppercase tracking-[0.18em] font-semibold text-navy-500 mb-2">Subject</span>
                <input
                  value={t.subject || ""}
                  onChange={(e) => updateTemplate(key, { subject: e.target.value })}
                  className={inputCls}
                  data-testid={`email-template-${key}-subject`}
                />
              </label>
              <div className="mt-4">
                <span className="block text-[11px] uppercase tracking-[0.18em] font-semibold text-navy-500 mb-2">Body</span>
                <RichText
                  value={t.html || ""}
                  onChange={(html) => updateTemplate(key, { html })}
                  placeholder="Write the acknowledgement message…"
                  testId={`email-template-${key}-body`}
                />
              </div>
            </div>
          );
        })}
      </section>

      {/* Test send + save */}
      <div className="mt-7 flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-2">
          <input
            type="email"
            value={testTo}
            onChange={(e) => setTestTo(e.target.value)}
            placeholder="you@example.com"
            className={`${inputCls} w-72`}
            data-testid="email-test-to"
          />
          <button
            onClick={sendTest}
            disabled={busy || !testTo}
            data-testid="email-send-test"
            className="inline-flex shrink-0 items-center gap-2 whitespace-nowrap px-5 py-3 rounded-full bg-white border border-navy-200 text-navy-800 text-sm font-semibold hover:bg-navy-50 transition-colors disabled:opacity-60"
          >
            <Send className="h-4 w-4" /> Send test
          </button>
        </div>
        <button
          onClick={save}
          disabled={busy}
          data-testid="email-save"
          className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-navy text-white text-sm font-semibold hover:bg-teal transition-colors disabled:opacity-60"
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save settings
        </button>
      </div>
    </div>
  );
}
